'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export type UseHistoryStateOptions<T> = {
  id?: string;
  initialState?: boolean;
  initialNestedState?: T;
};

export type UseHistoryStateReturnType<T> = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  nestedState: T | null;
  navigateToNestedState: (state: T) => void;
  goBackInNestedState: () => void;
};

/**
 * Hook that manages state with browser history integration.
 * Allows closing/opening overlays with browser back/forward buttons
 * without changing the visible URL.
 * Also supports nested states within modals for multi-level navigation.
 */
export function useHistoryState<T>(options: UseHistoryStateOptions<T> = {}): UseHistoryStateReturnType<T> {
  const { id = 'overlay', initialState = false, initialNestedState = null } = options;

  // Use state to track if the overlay is open
  const [isOpen, setIsOpen] = useState(initialState);

  // Use state to track nested state within the modal
  const [nestedState, setNestedState] = useState<T | null>(initialNestedState);

  // Keep track of whether we've added a history entry for the current state
  const hasAddedHistoryEntry = useRef(false);

  // Generate unique state keys for this instance
  const openStateKey = `${id}-open`;
  const nestedStateKey = `${id}-nested`;

  // Handle popstate events (browser back/forward)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Check if our state keys exist in the history state
      if (event.state) {
        // Handle open/close state
        if (openStateKey in event.state) {
          setIsOpen(!!event.state[openStateKey]);
          hasAddedHistoryEntry.current = !!event.state[openStateKey];
        } else if (isOpen) {
          // If our key isn't in the state but overlay is open, close it
          setIsOpen(false);
          hasAddedHistoryEntry.current = false;
        }

        // Handle nested state
        if (nestedStateKey in event.state) {
          setNestedState(event.state[nestedStateKey]);
        } else if (nestedState !== null) {
          setNestedState(null);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, nestedState, openStateKey, nestedStateKey]);

  // Function to open the overlay
  const open = useCallback(() => {
    if (!isOpen) {
      // Create a new history entry with our state
      const newState = { ...window.history.state };
      newState[openStateKey] = true;
      newState[nestedStateKey] = nestedState;

      // Push a new history entry without changing the URL
      window.history.pushState(newState, '', window.location.href);
      hasAddedHistoryEntry.current = true;

      setIsOpen(true);
    }
  }, [isOpen, nestedState, openStateKey, nestedStateKey]);

  // Function to close the overlay
  const close = useCallback(() => {
    if (isOpen) {
      if (hasAddedHistoryEntry.current) {
        // If we added a history entry for opening, go back to close
        window.history.back();
        hasAddedHistoryEntry.current = false;
      } else {
        // If we're open but didn't add a history entry (e.g., initial state was true)
        // Create a new history entry with our state set to false
        const newState = { ...window.history.state };
        newState[openStateKey] = false;
        newState[nestedStateKey] = null;

        window.history.pushState(newState, '', window.location.href);
        setIsOpen(false);
        setNestedState(null);
      }
    }
  }, [isOpen, openStateKey, nestedStateKey]);

  // Function to navigate to a nested state within the modal
  const navigateToNestedState = useCallback(
    (state: T) => {
      if (isOpen) {
        // Create a new history entry with our updated nested state
        const newState = { ...window.history.state };
        newState[openStateKey] = true;
        newState[nestedStateKey] = state;

        // Push a new history entry without changing the URL
        window.history.pushState(newState, '', window.location.href);

        setNestedState(state);
      }
    },
    [isOpen, openStateKey, nestedStateKey],
  );

  // Function to go back to previous nested state
  const goBackInNestedState = useCallback(() => {
    if (isOpen && nestedState !== null) {
      window.history.back();
    }
  }, [isOpen, nestedState]);

  // Function to toggle the overlay state
  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  return {
    isOpen,
    open,
    close,
    toggle,
    nestedState,
    navigateToNestedState,
    goBackInNestedState,
  };
}
